import { forwardRef, ReactNode, ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ChevronDown, AlertCircle, Info, CheckCircle2, X } from 'lucide-react'

/* ================================================================================
   TB Mukt Bharat — Consolidated Portal UI Kit
   Single source of truth for portal primitives. Token-driven via index.css
   HSL vars / .btn / .card classes. WCAG 2.2 AA: 44px touch targets, focus-visible,
   semantic markup, aria where needed. No duplicated Button/Card/EmptyState elsewhere.
   ================================================================================ */

type Variant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'gradient'
type Size = 'sm' | 'md' | 'lg'

const sizeClass: Record<Size, string> = {
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
}
const variantClass: Record<Variant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  accent: 'btn-accent',
  outline: 'btn-outline',
  ghost: 'btn-ghost',
  gradient: 'btn-gradient',
}

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  to?: string
  href?: string
  icon?: ReactNode
}

/** Button — renders <button>, router <Link>, or anchor depending on props. */
export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'primary', size = 'md', to, href, icon, children, className = '', ...rest },
  ref
) {
  const cls = `${variantClass[variant]} ${sizeClass[size]} ${className}`
  const content = (
    <>
      {icon && <span className="shrink-0" aria-hidden="true">{icon}</span>}
      {children}
    </>
  )
  if (to) {
    return (
      <Link to={to} className={cls} {...(rest as Record<string, unknown>)}>
        {content}
      </Link>
    )
  }
  if (href) {
    return (
      <a href={href} className={cls} {...(rest as Record<string, unknown>)}>
        {content}
      </a>
    )
  }
  return (
    <button ref={ref} className={cls} {...rest}>
      {content}
    </button>
  )
})

/** Badge — small status pill. */
export function Badge({
  children,
  tone = 'blue',
  className = '',
}: {
  children: ReactNode
  tone?: 'blue' | 'green' | 'amber' | 'slate' | 'teal'
  className?: string
}) {
  const tones: Record<string, string> = {
    blue: 'bg-[rgb(207_227_248)] text-[rgb(8_45_77)]',
    green: 'bg-[rgb(209_247_229)] text-[rgb(0_108_69)]',
    amber: 'bg-[rgb(254_243_199)] text-[rgb(120_53_15)]',
    slate: 'bg-snow-100 text-snow-700',
    teal: 'bg-[rgb(224_247_255)] text-[rgb(0_96_124)]',
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  )
}

/** Card — the one card primitive. */
export function Card({
  children,
  className = '',
  as: As = 'div',
  hover = false,
  ...rest
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'article' | 'section' | 'li'
  hover?: boolean
  [k: string]: unknown
}) {
  return (
    <As className={`${hover ? 'card-hover' : 'card'} ${className}`} {...rest}>
      {children}
    </As>
  )
}

/** Container — consistent max width (1280px) + responsive padding.
 *  1280px matches the design's max content width; wider screens get centered
 *  whitespace rather than overstretched content. */
export function Container({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
}

/** Section — page section with vertical rhythm + optional id for anchor nav. */
export function Section({
  children,
  className = '',
  id,
  ariaLabel,
}: {
  children: ReactNode
  className?: string
  id?: string
  ariaLabel?: string
}) {
  return (
    <section id={id} aria-label={ariaLabel} className={`py-12 sm:py-16 ${className}`}>
      {children}
    </section>
  )
}

/** SectionHeading — eyebrow + title + optional description, semantic h2. */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'left',
  as: As = 'h2',
}: {
  eyebrow?: string
  title: string
  description?: string
  align?: 'left' | 'center'
  as?: 'h1' | 'h2' | 'h3'
}) {
  return (
    <div className={`mb-8 ${align === 'center' ? 'text-center mx-auto max-w-2xl' : 'max-w-2xl'}`}>
      {eyebrow && (
        <p className="text-sm font-semibold uppercase tracking-wider text-[rgb(0_152_186)] mb-2">
          {eyebrow}
        </p>
      )}
      <As className="text-section-h2 font-editorial font-semibold text-snow-900 text-balance">
        {title}
      </As>
      {description && <p className="mt-3 text-snow-600 text-base sm:text-lg text-pretty">{description}</p>}
    </div>
  )
}

/** Breadcrumbs — nav with aria-label, last item is current page (no link). */
export function Breadcrumbs({ items }: { items: { name: string; path?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-snow-500 mb-4">
      <ol className="flex items-center flex-wrap gap-1.5">
        {items.map((it, i) => {
          const last = i === items.length - 1
          return (
            <li key={i} className="flex items-center gap-1.5">
              {it.path && !last ? (
                <Link to={it.path} className="hover:text-[rgb(15_76_129)] hover:underline">
                  {it.name}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined} className={last ? 'text-snow-700 font-medium' : ''}>
                  {it.name}
                </span>
              )}
              {!last && <ChevronRight className="w-3.5 h-3.5 text-snow-300" aria-hidden="true" />}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** StatTile — single statistic. value string is rendered verbatim; never invented. */
export function StatTile({
  value,
  label,
  verified = false,
}: {
  value: string
  label: string
  verified?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="stat-card text-center">
      {/* Placeholder values use a solid accessible color (snow-500 ≈ 4.6:1 on the
          glass card). The blue→cyan gradient (.stat-value) is reserved for verified
          figures — its cyan end fails AA contrast, so it must never carry a placeholder. */}
      <div className={verified ? 'stat-value text-4xl sm:text-5xl' : 'text-4xl sm:text-5xl font-bold text-snow-500'}>
        {value}
      </div>
      <div className="stat-label mt-1">{label}</div>
      {verified ? (
        <Badge tone="green" className="mt-2">{t('common.verified', 'Verified')}</Badge>
      ) : (
        <Badge tone="amber" className="mt-2">{t('common.dataRequired', 'Data required')}</Badge>
      )}
    </div>
  )
}

/** AudienceCard — the homepage "I am a…" grid tile. Links into a journey. */
export function AudienceCard({
  to,
  icon,
  title,
  description,
  links,
  tone = 'blue',
}: {
  to: string
  icon: ReactNode
  title: string
  description: string
  links: { label: string; path: string }[]
  tone?: 'blue' | 'green' | 'teal' | 'amber' | 'slate'
}) {
  const toneRing: Record<string, string> = {
    blue: 'hover:border-[rgb(166_200_242)] hover:shadow-[0_8px_30px_rgba(15,76,129,0.12)]',
    green: 'hover:border-[rgb(167_237_205)] hover:shadow-[0_8px_30px_rgba(0,168,107,0.12)]',
    teal: 'hover:border-[rgb(191_237_255)] hover:shadow-[0_8px_30px_rgba(0,180,216,0.12)]',
    amber: 'hover:border-[rgb(254_224_139)] hover:shadow-[0_8px_30px_rgba(255,154,0,0.12)]',
    slate: 'hover:border-snow-300 hover:shadow-lg',
  }
  const iconBg: Record<string, string> = {
    blue: 'bg-[rgb(232_241_252)] text-[rgb(15_76_129)]',
    green: 'bg-[rgb(232_252_243)] text-[rgb(0_138_88)]',
    teal: 'bg-[rgb(240_252_255)] text-[rgb(0_152_186)]',
    amber: 'bg-[rgb(255_251_235)] text-[rgb(185_83_9)]',
    slate: 'bg-snow-100 text-snow-700',
  }
  return (
    <Card hover className={`p-6 h-full flex flex-col group ${toneRing[tone]}`}>
      <Link to={to} className="flex items-start gap-4 mb-4" aria-label={title}>
        <span className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${iconBg[tone]}`}>
          {icon}
        </span>
        <h3 className="text-lg font-bold text-snow-900 mt-1.5 group-hover:text-[rgb(15_76_129)]">
          {title}
        </h3>
      </Link>
      <p className="text-snow-600 text-sm mb-4 flex-1">{description}</p>
      <ul className="space-y-1.5 mt-auto">
        {links.slice(0, 4).map((l, i) => (
          <li key={`${l.path}-${i}`}>
            <Link
              to={l.path}
              className="flex items-center gap-1 text-sm text-snow-700 hover:text-[rgb(15_76_129)] group/link"
            >
              <ChevronRight className="w-3.5 h-3.5 text-snow-300 group-hover/link:text-[rgb(0_180_216)]" aria-hidden="true" />
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/** FAQAccordion — accessible disclosure list. Keyboard + aria handled. */
export function FAQAccordion({ items }: { items: { q: string; a: ReactNode }[] }) {
  return (
    <div className="divide-y divide-snow-200 rounded-2xl border border-snow-200 bg-white overflow-hidden">
      {items.map((it, i) => (
        <details key={i} className="group">
          <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none font-semibold text-snow-900 hover:bg-snow-50">
            <span>{it.q}</span>
            <ChevronDown className="w-5 h-5 shrink-0 text-snow-400 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="px-5 pb-5 text-snow-600 text-sm leading-relaxed prose prose-sm max-w-none">
            {it.a}
          </div>
        </details>
      ))}
    </div>
  )
}

/** Alert — inline notice. tone maps to color; never color-only (icon + label). */
export function Alert({
  tone = 'info',
  title,
  children,
  className = '',
}: {
  tone?: 'info' | 'success' | 'warning'
  title: string
  children?: ReactNode
  className?: string
}) {
  const map = {
    info: { icon: <Info className="w-5 h-5" />, cls: 'bg-[rgb(240_252_255)] border-[rgb(191_237_255)] text-[rgb(0_96_124)]' },
    success: { icon: <CheckCircle2 className="w-5 h-5" />, cls: 'bg-[rgb(232_252_243)] border-[rgb(167_237_205)] text-[rgb(0_108_69)]' },
    warning: { icon: <AlertCircle className="w-5 h-5" />, cls: 'bg-[rgb(255_251_235)] border-[rgb(254_224_139)] text-[rgb(120_53_15)]' },
  }
  const m = map[tone]
  return (
    <div role="note" className={`flex gap-3 p-4 rounded-xl border ${m.cls} ${className}`}>
      <span className="shrink-0" aria-hidden="true">{m.icon}</span>
      <div>
        <p className="font-semibold">{title}</p>
        {children && <div className="text-sm mt-1 opacity-90">{children}</div>}
      </div>
    </div>
  )
}

/** EmptyState — reusable, never a blank screen. */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="text-center py-16 px-6">
      {icon && (
        <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-snow-100 flex items-center justify-center text-snow-400" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-snow-900">{title}</h3>
      {description && <p className="text-snow-500 mt-2 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/** LoadingSkeleton — shimmer placeholder. */
export function LoadingSkeleton({ count = 3, className = '' }: { count?: number; className?: string }) {
  const { t } = useTranslation()
  return (
    <div className={`space-y-4 ${className}`} aria-busy="true" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card p-6 animate-pulse">
          <div className="h-4 w-1/3 bg-snow-200 rounded mb-3" />
          <div className="h-3 w-full bg-snow-100 rounded mb-2" />
          <div className="h-3 w-2/3 bg-snow-100 rounded" />
        </div>
      ))}
      <span className="sr-only">{t('common.loading', 'Loading content…')}</span>
    </div>
  )
}

/** FormField — label + input + error + help text, accessible. */
export function FormField({
  label,
  htmlFor,
  required,
  error,
  helpText,
  children,
}: {
  label: string
  htmlFor: string
  required?: boolean
  error?: string
  helpText?: string
  children: ReactNode
}) {
  const errId = `${htmlFor}-error`
  const helpId = `${htmlFor}-help`
  const describedBy = [error && errId, helpText && helpId].filter(Boolean).join(' ') || undefined
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-snow-800">
        {label}
        {required && (
          <span className="text-danger-500 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {/** children must wire id, aria-invalid, aria-describedby themselves */}
      <div data-describedby={describedBy} data-error={error}>
        {children}
      </div>
      {helpText && !error && (
        <p id={helpId} className="text-xs text-snow-500">
          {helpText}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-xs text-danger-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  )
}

/** PullQuote — editorial human-voice block. Semantic <figure>/<blockquote>/<figcaption>.
 *  Warm ground, Fraunces italic quote, decorative quote-mark (aria-hidden).
 *  `quote`/`author`/`role` are passed by the caller from a real Testimonial record —
 *  the caller is responsible for never fabricating a named person (use the honest
 *  EmptyState path when no published testimonial exists). */
export function PullQuote({
  quote,
  author,
  role,
  to,
  linkLabel,
  className = '',
}: {
  quote: string
  author?: string
  role?: string
  to?: string
  linkLabel?: string
  className?: string
}) {
  const { t } = useTranslation()
  const label = linkLabel ?? t('common.readStory', 'Read story')
  return (
    <figure className={`relative rounded-2xl bg-warm-50 border border-warm-200 p-6 sm:p-8 ${className}`}>
      <span className="quote-mark absolute top-2 left-4 text-6xl sm:text-7xl" aria-hidden="true">&ldquo;</span>
      <blockquote className="relative">
        <p className="font-editorial italic text-lg sm:text-xl leading-relaxed text-snow-800 text-pretty">
          {quote}
        </p>
      </blockquote>
      {(author || role) && (
        <figcaption className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {author && <span className="font-semibold text-snow-900">{author}</span>}
          {role && (
            <>
              <span className="text-snow-400" aria-hidden="true">·</span>
              <span className="text-snow-600">{role}</span>
            </>
          )}
        </figcaption>
      )}
      {to && (
        <Link
          to={to}
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-human-700 hover:text-human-800 hover:underline"
        >
          {label}
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      )}
    </figure>
  )
}

/** ProseContainer — readable long-form content (articles). */
export function ProseContainer({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`prose prose-slate prose-lg max-w-none prose-headings:text-snow-900 prose-a:text-[rgb(15_76_129)] prose-strong:text-snow-900 prose-li:my-1 prose-headings:scroll-mt-24 ${className}`}
    >
      {children}
    </div>
  )
}

export { X as CloseIcon }